import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { clearCart } = useCart();
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [orderDetails, setOrderDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, currentAttempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (currentAttempt >= maxAttempts) {
      setStatus('error');
      return;
    }

    setAttempts(currentAttempt + 1);

    try {
      const response = await axios.get(`${API}/checkout/status/${sid}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setOrderDetails(data);
        // Clear cart after successful payment
        await clearCart();
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, currentAttempt + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (currentAttempt < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, currentAttempt + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="py-20 bg-gray-50 min-h-screen flex items-center">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="py-12 text-center">
                {status === 'checking' && (
                  <>
                    <Loader2 className="h-16 w-16 text-amber-600 mx-auto mb-6 animate-spin" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Verifying your payment...
                    </h2>
                    <p className="text-gray-600 mb-2">
                      Please wait while we confirm your order
                    </p>
                    <p className="text-sm text-gray-500">
                      Attempt {attempts} of 5
                    </p>
                  </>
                )}

                {status === 'success' && (
                  <>
                    <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-6" />
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      Payment Successful!
                    </h2>
                    <p className="text-xl text-gray-600 mb-8">
                      Thank you for your purchase from Trump of the Lord Ministries
                    </p>
                    
                    {orderDetails && (
                      <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
                        <h3 className="font-semibold text-gray-900 mb-4">Order Details:</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Order ID:</span>
                            <span className="font-medium">{orderDetails.order_id}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Amount:</span>
                            <span className="font-medium">${orderDetails.amount?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <span className="font-medium text-green-600">Paid</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <p className="text-gray-600 mb-8">
                      A confirmation email has been sent to your inbox with order details and digital access information.
                    </p>
                    
                    <p className="text-sm text-gray-500 mb-8">
                      Questions? Contact us at <a href="mailto:support@trumpofthelord.com" className="text-blue-600 hover:text-blue-700">support@trumpofthelord.com</a>
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Link to="/store">
                        <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                          Continue Shopping
                        </Button>
                      </Link>
                      <Link to="/">
                        <Button variant="outline">
                          Return to Home
                        </Button>
                      </Link>
                    </div>
                  </>
                )}

                {status === 'error' && (
                  <>
                    <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                      <span className="text-3xl">⚠️</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Payment Verification Failed
                    </h2>
                    <p className="text-gray-600 mb-8">
                      We couldn't verify your payment status. If you were charged, please contact us at <a href="mailto:support@trumpofthelord.com" className="text-blue-600 hover:text-blue-700">support@trumpofthelord.com</a> with your session ID.
                    </p>
                    {sessionId && (
                      <p className="text-sm text-gray-500 mb-8 font-mono bg-gray-100 p-3 rounded">
                        Session ID: {sessionId}
                      </p>
                    )}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Link to="/cart">
                        <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                          Return to Cart
                        </Button>
                      </Link>
                      <Link to="/about">
                        <Button variant="outline">
                          Contact Support
                        </Button>
                      </Link>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckoutSuccess;
